import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Clock, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Container, ArrowLink } from "@/components/shared/PageHeader";
import { ArticleBody } from "@/components/shared/ArticleBody";
import { CaptionedFigure } from "@/components/shared/CaptionedFigure";
import { PostCard, postDate } from "@/components/shared/PostCard";
import { NewsletterSignup } from "@/components/shared/NewsletterSignup";
import { FadeUp } from "@/components/shared/FadeUp";
import { Seo, shareUrl } from "@/components/shared/Seo";
import { SIZES } from "@/components/shared/Img";
import { fetchPost } from "@/lib/api";
import NotFound from "@/pages/public/NotFound";

const ShareButton = ({ title, url }) => {
  const [done, setDone] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title, url });
      else { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 2000); }
    } catch { /* cancelled */ }
  };
  return <Button variant="outline" size="sm" className="h-9 rounded-full" onClick={share} data-testid="post-share-button">{done ? <><Check className="h-3.5 w-3.5" /> Lenkje kopiert</> : <><Share2 className="h-3.5 w-3.5" /> Del</>}</Button>;
};

const articleJsonLd = (post, image) => ({
  "@context": "https://schema.org", "@type": "NewsArticle", headline: post.title.slice(0, 110), description: post.seo_description || post.lead || post.excerpt,
  image: [image], inLanguage: "nn", datePublished: post.published_at, dateModified: post.updated_at || post.published_at, mainEntityOfPage: `${window.location.origin}/nytt/${post.slug}`,
  keywords: post.tags?.length ? post.tags.join(", ") : undefined,
  author: { "@type": "Organization", name: "Halsnøy Kulturhus SA", url: window.location.origin }, publisher: { "@type": "Organization", name: "Halsnøy Kulturhus SA", url: window.location.origin },
});

/** /nytt/:slug – one story: title first, then the picture, then the words at a comfortable measure. */
export default function NewsPost() {
  const { slug } = useParams();
  const { data: post, isLoading, isError } = useQuery({ queryKey: ["post", slug], queryFn: () => fetchPost(slug), retry: false });
  if (isError) return <NotFound />;
  if (isLoading || !post) return <Container className="py-16"><Skeleton className="h-6 w-40" /><Skeleton className="mt-6 h-20 w-full max-w-3xl" /><Skeleton className="mt-10 aspect-[16/9] w-full rounded-[20px]" /></Container>;
  const og = { kind: "nytt", id: post.slug };
  return (
    <article data-testid="post-page">
      <Seo title={post.title} description={post.seo_description || post.excerpt} og={og} imageAlt={post.cover_caption || post.title} type="article" publishedAt={post.published_at} modifiedAt={post.updated_at}
        jsonLd={articleJsonLd(post, `${window.location.origin}/api/og/nytt/${encodeURIComponent(post.slug)}.jpg`)} breadcrumbs={[["Heim", "/"], ["Nytt frå huset", "/nytt"], [post.title, null]]} />
      <Container className="pt-8 sm:pt-12">
        <Link to="/nytt" className="inline-flex items-center gap-1.5 text-[14px] font-medium text-muted-foreground u-hover hover:text-foreground" data-testid="post-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Nytt frå huset</Link>
        <header className="mt-8 grid gap-6 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-9">
            <p className="t-mono flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground" data-testid="post-meta">
              <span>{postDate(post)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.reading_minutes} min lesing</span>
              {post.author?.name && <span>Av {post.author.name}</span>}
            </p>
            <h1 className="t-h1 mt-4 max-w-4xl text-balance" data-testid="post-title">{post.title}</h1>
            {post.lead && <p className="t-lead mt-6 max-w-2xl text-muted-foreground text-pretty" data-testid="post-lead">{post.lead}</p>}
          </div>
          <div className="flex items-start gap-2 lg:col-span-3 lg:justify-end"><ShareButton title={post.title} url={shareUrl("nytt", post.slug)} /></div>
        </header>
      </Container>
      {post.cover && (
        <Container className="mt-10 sm:mt-14">
          <FadeUp><CaptionedFigure src={post.cover} caption={post.cover_caption} alt={post.cover_caption || post.title} width={1600} sizes={SIZES.full} imgClassName="aspect-[3/2] sm:aspect-[21/9]" radius="rounded-[20px]" testId="post-cover" /></FadeUp>
        </Container>
      )}
      <Container className="mt-12 sm:mt-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <ArticleBody html={post.body} className="lg:col-span-8 lg:col-start-3" testId="post-body" />
        </div>
        {post.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-2 lg:ml-[calc(100%/6)]" data-testid="post-tags">
            <span className="t-mono mr-2 text-muted-foreground">Stikkord</span>
            {post.tags.map((t) => <Link key={t} to={`/nytt?stikkord=${encodeURIComponent(t)}`} className="h-8 rounded-full border border-border bg-card px-3.5 text-[13px] font-medium capitalize leading-8 transition-colors hover:border-foreground" data-testid={`post-tag-${t}`}>{t}</Link>)}
          </div>
        )}
      </Container>
      <section className="mt-16 border-t border-border sm:mt-20" data-testid="post-newsletter">
        <Container className="py-14 sm:py-16">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6"><p className="t-mono text-muted-foreground">Nyheitsbrev</p><h2 className="t-h3 mt-3 text-balance">Vil du ha neste sak rett i innboksen?</h2></div>
            <div className="lg:col-span-5 lg:col-start-8"><NewsletterSignup tone="light" source="arrangement" testId="post-newsletter" /></div>
          </div>
        </Container>
      </section>
      {post.more?.length > 0 && (
        <section className="border-t border-border" data-testid="post-more">
          <Container className="py-14 sm:py-16">
            <div className="mb-8 flex items-end justify-between gap-4"><h2 className="t-h2">Fleire saker</h2><ArrowLink to="/nytt" data-testid="post-more-link">Alle saker</ArrowLink></div>
            <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">{post.more.map((p) => <PostCard key={p.id} post={p} />)}</div>
          </Container>
        </section>
      )}
    </article>
  );
}
